import React, { useMemo } from 'react';
import { Alert, Select, Typography } from 'antd';

import FilterBuilder from '../common/FilterBuilder';
import FilterChips from '../common/FilterChips';
import { optionSearchFilter } from '../Input';
import { ReadBack } from './TriggerSentence';
import { columnsOfGroup } from '../../utils/entryRules';
import {
	MANUAL_RESOLVE_OPTIONS,
	RESOLVE_KINDS,
	describeResolveDraft,
	resolveOptionsFor,
} from './notificationEditorModel';

const { Text } = Typography;

const labelOf = element => element?.label?.singular || element?.name || '';

// What a criterion can be BOUND to the subject by: a lookup at the same
// record, or a person. An equality binding is the only thing that reduces to
// one indexed join (Decision 4) — a criterion that needs anything else is
// refused at authoring by the API, with the reason shown.
const BINDABLE_TYPES = ['card-lookup', 'user'];

/**
 * Panel 5 — **It stays open until**: the criterion that closes an alert, and
 * how it relates to the record that opened it.
 *
 * Four answers, which are the four the engine actually implements: nothing
 * (it is sent and done), a response, an item created matching a query, or an
 * element filled in. The fifth case is a `resolve.kind` this release has never
 * heard of — a later version's, or an API-authored one — and it renders as a
 * sentence rather than being silently rewritten into one of the four.
 *
 * **What is bound to the subject** is the load-bearing control. "A med log for
 * *this* resident today" is a shared residual condition plus a frozen equality
 * binding, and that reduction is the only reason ten thousand open alerts cost
 * what one costs. So the criterion asks which values travel from the record
 * that opened it, in words, rather than leaving an author to discover at 3am
 * that their criterion matched somebody else's record.
 *
 * **A column of that entry** (KPR-874): a reminder about a date in each entry
 * of a repeating group (`watchedGroup`) may wait on a column of the entry it
 * told about — "It stays open until Given is filled in on that dose". No card
 * to pick and nothing to bind: the entry is the one the notice was about.
 */
export default function ResolveCriterion({
	draft,
	onChange,
	cardOptions = [],
	elementsFor,
	fieldsFor,
	collectionId,
	watchedGroup = null,
}) {
	const resolve = draft.resolve || {};
	const patch = next => onChange({ ...draft, resolve: { ...resolve, ...next } });

	const resolveElements = useMemo(
		() => (resolve.cardId && elementsFor ? elementsFor(resolve.cardId) : []),
		[resolve.cardId, elementsFor]
	);
	const elementOptions = resolveElements
		.filter(el => el?.name)
		.map(el => ({ value: el.name, label: labelOf(el), searchText: labelOf(el) }));
	const bindOptions = resolveElements
		.filter(el => el?.name && BINDABLE_TYPES.includes(el.dataType))
		.map(el => ({ value: el.name, label: labelOf(el), searchText: labelOf(el) }));
	const fields = useMemo(
		() => (resolve.cardId && fieldsFor ? fieldsFor(resolve.cardId) : []),
		[resolve.cardId, fieldsFor]
	);

	const needsCard = resolve.kind === RESOLVE_KINDS.QUERY_ITEM_CREATED || resolve.kind === RESOLVE_KINDS.ELEMENT_POPULATED;
	const elementLabel = name => labelOf(resolveElements.find(el => el?.name === name)) || name;

	// The watched group's columns (KPR-874), only while the reminder watches one.
	const entryGroup = watchedGroup && watchedGroup.name === draft.entryGroup ? watchedGroup : null;
	const groupLabel = entryGroup ? labelOf(entryGroup) : '';
	const columns = useMemo(() => columnsOfGroup(entryGroup), [entryGroup]);
	const columnOptions = columns.map(col => ({ value: col.name, label: labelOf(col), searchText: labelOf(col) }));
	const columnLabel = name => labelOf(columns.find(col => col.name === name)) || name;
	const kindOptions = resolveOptionsFor(draft, { groupLabel });
	// A stored column the group no longer has is shown as unpicked, not as a raw name.
	const pickedColumn = columns.some(col => col.name === resolve.column) ? resolve.column : undefined;
	const onEntry = resolve.kind === RESOLVE_KINDS.ENTRY_POPULATED;

	return (
		<section className="form-panel notification-editor__panel" data-testid="notification-editor-resolve">
			<div className="micro-label form-panel__kicker">It stays open until</div>

			{resolve.unknown ? (
				<Alert
					type="info"
					showIcon
					message="This one closes on a rule this editor does not draw"
					description={`It is kept exactly as it is (${resolve.unknown.kind}). Saving here will not change it.`}
				/>
			) : (<>
				<div className="item-form-fields">
					<div className="item-form-field field--full">
						<label className="d-block" htmlFor="notification-resolve-kind">What closes it</label>
						<Select
							id="notification-resolve-kind"
							value={resolve.kind}
							onChange={kind => patch({ kind })}
							options={kindOptions}
						/>
					</div>

					{onEntry && entryGroup ? (
						<div className="item-form-field field--half">
							<label className="d-block" id="notification-resolve-column-label">Which column</label>
							<Select
								aria-labelledby="notification-resolve-column-label"
								placeholder="Pick a column"
								value={pickedColumn}
								onChange={column => patch({ column })}
								options={columnOptions}
								showSearch
								filterOption={optionSearchFilter}
							/>
							<div className="form-panel__hint">
								Filled in on the entry the notice was about — another entry&apos;s does not close it.
							</div>
						</div>
					) : null}

					{needsCard ? (
						<div className="item-form-field field--half">
							<label className="d-block" id="notification-resolve-card-label">Which record</label>
							<Select
								aria-labelledby="notification-resolve-card-label"
								placeholder="Pick a card"
								value={resolve.cardId || undefined}
								onChange={cardId => patch({ cardId, element: '', kql: '', bindOn: [] })}
								options={cardOptions}
								showSearch
								filterOption={optionSearchFilter}
								popupClassName="card-picker-dropdown"
							/>
						</div>
					) : null}

					{resolve.kind === RESOLVE_KINDS.ELEMENT_POPULATED ? (
						<div className="item-form-field field--half">
							<label className="d-block" id="notification-resolve-element-label">Which element</label>
							<Select
								aria-labelledby="notification-resolve-element-label"
								placeholder={resolve.cardId ? 'Pick an element' : 'Pick a card first'}
								value={resolve.element || undefined}
								onChange={element => patch({ element })}
								options={elementOptions}
								disabled={!resolve.cardId}
								showSearch
								filterOption={optionSearchFilter}
							/>
						</div>
					) : null}

					{needsCard && resolve.cardId ? (
						<div className="item-form-field field--full">
							<label className="d-block" id="notification-resolve-bind-label">For the same…</label>
							<Select
								aria-labelledby="notification-resolve-bind-label"
								mode="multiple"
								placeholder="Any matching record closes it"
								value={resolve.bindOn}
								onChange={bindOn => patch({ bindOn })}
								options={bindOptions}
							/>
							<div className="form-panel__hint">
								The values that travel from the record that opened the alert. Without one,
								any matching record anywhere closes it — which is rarely what a shift means.
							</div>
						</div>
					) : null}

					{resolve.kind !== RESOLVE_KINDS.NONE ? (
						<div className="item-form-field field--half">
							<label className="d-block" htmlFor="notification-resolve-manual">Who can close it by hand</label>
							<Select
								id="notification-resolve-manual"
								value={resolve.allowManualResolve}
								onChange={allowManualResolve => patch({ allowManualResolve })}
								options={MANUAL_RESOLVE_OPTIONS}
							/>
						</div>
					) : null}
				</div>

				{resolve.kind === RESOLVE_KINDS.QUERY_ITEM_CREATED && resolve.cardId ? (
					<div className="notification-editor__condition">
						<div className="notification-editor__condition-row">
							<Text type="secondary">Matching</Text>
							<FilterBuilder
								collectionId={collectionId}
								scopeKey={`notification-resolve:${collectionId || 'global'}`}
								fields={fields}
								query={resolve.kql || ''}
								onApply={text => patch({ kql: text || '' })}
								emptyText="Any record of this card closes it."
								placeholder="status = done"
								disallowMe
							/>
						</div>
						{resolve.kql ? (
							<FilterChips query={resolve.kql} fields={fields} readOnly className="notification-editor__chips" />
						) : null}
					</div>
				) : null}
			</>)}

			<ReadBack text={describeResolveDraft(draft, {
				cardLabel: cardOptions.find(option => option.value === resolve.cardId)?.card?.name,
				elementLabel,
				groupLabel,
				columnLabel,
			})} />
		</section>
	);
}
